import { shallowEqual, useDispatch } from 'react-redux';

import {
  Anchor,
  Box,
  Divider,
  Heading,
  Image,
  List,
  Modal,
  Paragraph,
  Toast,
  useToast,
} from '@zillow/constellation-10';
import {
  useRentalManagerUserPropertyEditWebApiHandler_displayTotalMonthlyPriceWithFee,
  useRentalManagerUserPropertyWebAnalyticsWebApiHandler_get,
} from '@zg-rentals/rental-manager-env';

import { useSelector } from '../../../selectors/useSelector';

import PropertiesActions from '../../../actions/PropertiesActions';

import ClipboardImage from '../../../images/totalMonthlyPriceWithFee/total-monthly-price-with-fee-acknowledgement-clipboard.svg';

import SingleFamilyCSEvents from '../../../model/ClickstreamEvent/TotalMonthlyPriceWithFee/SingleFamilyCSEvents';

import ModalFooter from '../ModalFooter';

type Props = {
  onClose: () => void;
  onSubmit: () => void;
  isSubmitLoading?: boolean;
};

const SFTotalMonthlyPriceWithFeeAcknowledgementModal = ({ onClose, onSubmit, isSubmitLoading = false }: Props) => {
  const { enqueueToast } = useToast();

  const dispatch = useDispatch();

  const { listingAlias, modularListingProgressStep, user } = useSelector(
    (state) => ({
      listingAlias: state.properties?.currentProperty?.listingAlias,
      modularListingProgressStep: state.properties?.currentProperty?.modularListingProgress?.step,
      user: state?.user,
    }),
    shallowEqual,
  );

  const { data: { lease, ...webAnalyticsData } = {} } = useRentalManagerUserPropertyWebAnalyticsWebApiHandler_get({
    listingAlias,
    includeLeases: true,
  });

  const { mutateAsync: setDisplayTotalMonthlyPriceWithFee } =
    useRentalManagerUserPropertyEditWebApiHandler_displayTotalMonthlyPriceWithFee();

  const csEventsHandler = ({ triggerSource }: { triggerSource: string }) => {
    SingleFamilyCSEvents.TotalPriceModalInteraction({
      user,
      currentProperty: webAnalyticsData,
      lease,
      triggerSource,
      triggerLocation: !modularListingProgressStep?.includes('review')
        ? 'posting_path'
        : 'posting_path_review|off_market_listing',
    });
  };

  const onSubmitHandler = async () => {
    csEventsHandler({ triggerSource: 'button_to_confirm' });

    try {
      await setDisplayTotalMonthlyPriceWithFee({
        getParams: { listingAlias, displayTotalMonthlyPriceWithFee: true },
      });

      dispatch(PropertiesActions.updateCurrentPropertyDisplayTotalMonthlyPriceWithFee(true));

      onSubmit();
    } catch (e) {
      enqueueToast(
        <Toast tone="critical">
          There was an error enabling display total monthly price with fee. Please try again.
        </Toast>,
      );
    }
  };

  return (
    <Modal.Root
      open={true}
      onOpenChange={(open) =>
        !open &&
        (() => {
          csEventsHandler({ triggerSource: 'button_x_to_close' });
          onClose();
        })()
      }
      size={{ base: 'fullScreen', md: 'md' }}
    >
      <Modal.Portal>
        <Modal.Backdrop>
          <Modal.Content>
            <Modal.Body>
              <div data-testid="SFTotalMonthlyPriceWithFeeAcknowledgementModal">
                <Box css={{ marginBottom: '800' }}>
                  <Image src={ClipboardImage} alt="TotalMonthlyPriceWithFeeAcknowledgementClipboard" />
                </Box>

                <Heading level={4} css={{ marginBottom: '400' }}>
                  Confirm the fee information is complete and accurate
                </Heading>

                <div>
                  <List.Root appearance="bullet">
                    <List.Item>
                      Your area has fee transparency regulations that require you to disclose a &ldquo;total
                      price&rdquo; to your renters.{' '}
                      <Anchor
                        target="_blank"
                        href="https://www.zillow.com/rentals-network/pricing-transparency/"
                        onClick={() => {
                          SingleFamilyCSEvents.LearnMore({
                            user,
                            currentProperty: webAnalyticsData,
                            triggerObject: 'zrm_costs_and_fees_tmp_modal',
                            triggerLocation: !modularListingProgressStep?.includes('review')
                              ? 'posting_path'
                              : 'posting_path_review',
                          });
                        }}
                        data-eventid={SingleFamilyCSEvents.LearnMore.data_event_id}
                      >
                        Learn more
                      </Anchor>
                    </List.Item>

                    <List.Item>
                      To help renters understand the total cost of living in this home, any required monthly fees will
                      be combined with base rent and shown as the list price.
                    </List.Item>

                    <List.Item>
                      A &ldquo;total price&rdquo; label will also be added to your listing to confirm fee transparency.
                    </List.Item>
                  </List.Root>

                  <Box css={{ marginTop: '400' }}>
                    <Paragraph>
                      By selecting &ldquo;Confirm,&rdquo; you confirm you&apos;ve accurately entered required monthly
                      fees and you want to enable &ldquo;total price&rdquo; display.
                    </Paragraph>
                  </Box>
                </div>
              </div>
            </Modal.Body>
            <Divider />
            <Modal.Footer>
              <ModalFooter
                cancelText="Go back"
                onCancel={() => {
                  csEventsHandler({ triggerSource: 'button_to_go_back' });

                  onClose();
                }}
                submitText="Confirm"
                isSubmitting={isSubmitLoading}
                onSubmit={onSubmitHandler}
                onCancelDataEventId={SingleFamilyCSEvents.TotalPriceModalInteraction.data_event_id}
                onSubmitDataEventId={SingleFamilyCSEvents.TotalPriceModalInteraction.data_event_id}
              />
            </Modal.Footer>
          </Modal.Content>
        </Modal.Backdrop>
      </Modal.Portal>
    </Modal.Root>
  );
};

export default SFTotalMonthlyPriceWithFeeAcknowledgementModal;
